import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, LayoutGrid, Plus } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';

import { CreateScenarioForm } from './CreateScenarioForm';
import type {
  ConsensusScenarioComparison,
  ConsensusScenarioListItem,
  ConsensusScenarioRow,
  CreateScenarioPayload,
} from '../types/consensus.types';
import { formatChartBucketLabel } from '../utils/chartLabels';
import { resolveUiLocale, formatUiNumber } from '@/utils/uiLocale';

type ViewMode = 'macro' | 'monthly';
type Metric = 'units' | 'value';

interface Props {
  comparison: ConsensusScenarioComparison;
  loading?: boolean;
  selectedScenarioIds: number[];
  onSelectedScenarioIdsChange: (ids: number[]) => void;
  showCreateForm?: boolean;
  creatingScenario?: boolean;
  onToggleCreateForm?: () => void;
  onCreateScenario?: (payload: CreateScenarioPayload) => Promise<void>;
}

function formatNumber(value: number, digits: number, language?: string | null): string {
  return formatUiNumber(value, { maximumFractionDigits: digits }, language);
}

function cellClass(tone: string): string {
  if (tone === 'up') return 'bg-sky-100 text-sky-900';
  if (tone === 'down') return 'bg-rose-100 text-rose-900';
  return 'bg-white text-gray-800';
}

function formatMacroPeriodLabel(
  periodKey: string,
  granularity: string | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  locale: string,
): string {
  if (periodKey === 'total') return t('consensus.scenarioColPeriodTotal');
  if (granularity === 'quarter' && periodKey.includes('-Q')) {
    const [, q] = periodKey.split('-Q');
    const year = periodKey.split('-Q')[0];
    return t('consensus.scenarioColQuarter', { quarter: q, year });
  }
  return formatChartBucketLabel(periodKey, 'month', locale);
}

function ScenarioDataRow({
  row,
  periodKeys,
  stripe,
  viewMode,
  metric,
}: {
  row: ConsensusScenarioRow;
  periodKeys: string[];
  columnMeta: Map<string, { granularity?: string }>;
  stripe: boolean;
  viewMode: ViewMode;
  metric: Metric;
}) {
  const { t, i18n } = useTranslation();
  const uiLocale = resolveUiLocale(i18n.language);
  const { formatRounded } = useCurrency();
  const formatMoney = (value: number) => formatRounded(value);
  const formatSignedMoney = (value: number) => `${value > 0 ? '+' : ''}${formatMoney(value)}`;
  const cellByPeriod = useMemo(() => {
    const cells = viewMode === 'macro' ? row.macro_cells ?? [] : row.cells;
    return new Map(cells.map((c) => [c.period, c]));
  }, [row, viewMode]);

  return (
    <tr className={stripe ? 'bg-sky-50/30' : 'bg-white'}>
      <td className="sticky left-0 z-10 min-w-[200px] border-r border-gray-100 bg-inherit px-3 py-2 text-sm font-medium text-gray-900">
        {row.scenario_id == null ? t('consensus.baseScenario') : row.scenario_name}
      </td>
      {periodKeys.map((period) => {
        const cell = cellByPeriod.get(period);
        if (!cell) {
          return (
            <td key={period} className="px-2 py-2 text-right text-xs text-gray-400">
              —
            </td>
          );
        }
        const vsPy = cell.vs_prior_year_pct;
        const isValue = metric === 'value';
        const deltaValue = cell.delta_value ?? 0;
        return (
          <td
            key={period}
            className={`px-2 py-2 text-right text-xs tabular-nums ${cellClass(cell.tone)}`}
            title={
              viewMode === 'macro' && period === 'total'
                ? `${t('consensus.scenarioVsPriorYear')}: ${formatNumber(vsPy ?? 0, 1, uiLocale)}%`
                : `Δ ${formatNumber(cell.delta_pct, 1, uiLocale)}%`
            }
          >
            <div className="font-medium">
              {isValue ? formatMoney(cell.demand_value ?? 0) : formatNumber(cell.demand_qty, 0, uiLocale)}
            </div>
            {isValue ? (
              deltaValue !== 0 ? (
                <div className="text-[10px] opacity-80">
                  {t('consensus.scenarioVsBaseShort')}: {formatSignedMoney(deltaValue)}
                </div>
              ) : null
            ) : viewMode === 'macro' && period === 'total' && vsPy != null && vsPy !== 0 ? (
              <div className="text-[10px] opacity-80">
                {t('consensus.scenarioVsPriorYearShort')}: {vsPy > 0 ? '+' : ''}
                {formatNumber(vsPy, 1, uiLocale)}%
              </div>
            ) : cell.delta_pct !== 0 ? (
              <div className="text-[10px] opacity-80">
                {t('consensus.scenarioVsBaseShort')}: {cell.delta_pct > 0 ? '+' : ''}
                {formatNumber(cell.delta_pct, 1, uiLocale)}%
              </div>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}

function ScenarioPicker({
  available,
  selectedIds,
  onChange,
}: {
  available: ConsensusScenarioListItem[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { t } = useTranslation();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (available.length === 0) {
    return <p className="text-xs text-gray-500">{t('consensus.scenarioPickerEmpty')}</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {t('consensus.scenarioPickerLabel')}
      </span>
      {available.map((scenario) => {
        const active = selectedSet.has(scenario.id);
        return (
          <label
            key={scenario.id}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
              active
                ? 'border-stocker-primary/30 bg-stocker-primary/10 text-stocker-primary'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={active}
              onChange={() => toggle(scenario.id)}
            />
            <span className="font-medium">{scenario.name}</span>
            <span className="text-[10px] opacity-70">{scenario.scenario_type}</span>
          </label>
        );
      })}
      <button
        type="button"
        className="text-xs text-stocker-primary hover:underline"
        onClick={() => onChange(available.map((s) => s.id))}
      >
        {t('consensus.scenarioSelectAll')}
      </button>
      <button
        type="button"
        className="text-xs text-gray-500 hover:underline"
        onClick={() => onChange([])}
      >
        {t('consensus.scenarioSelectNone')}
      </button>
    </div>
  );
}

export function ConsensusScenarioGrid({
  comparison,
  loading,
  selectedScenarioIds,
  onSelectedScenarioIdsChange,
  showCreateForm,
  creatingScenario,
  onToggleCreateForm,
  onCreateScenario,
}: Props) {
  const { t, i18n } = useTranslation();
  const { symbol } = useCurrency();
  const [viewMode, setViewMode] = useState<ViewMode>('macro');
  const [metric, setMetric] = useState<Metric>('units');

  const { baseRow, scenarios, macroPeriods, monthlyPeriods, macroColumns, horizon, available } =
    useMemo(() => {
      const meta = comparison.macro_columns ?? [];
      const horizonCol = meta.find((c) => c.granularity === 'meta');
      const list = comparison.available_scenarios ?? [];
      return {
        baseRow: comparison.base_row,
        scenarios: comparison.scenarios ?? [],
        available: list,
        macroPeriods: comparison.macro_periods ?? [],
        monthlyPeriods: comparison.periods ?? [],
        macroColumns: meta,
        horizon: horizonCol,
      };
    }, [comparison]);

  const selectedSet = useMemo(() => new Set(selectedScenarioIds), [selectedScenarioIds]);

  const visibleScenarios = useMemo(
    () => scenarios.filter((row) => row.scenario_id != null && selectedSet.has(row.scenario_id)),
    [scenarios, selectedSet],
  );

  const columnMeta = useMemo(
    () => new Map(macroColumns.map((c) => [c.key, c])),
    [macroColumns],
  );

  const periodKeys = viewMode === 'macro' ? macroPeriods : monthlyPeriods;
  const allRows = [baseRow, ...visibleScenarios];

  const horizonLabel =
    horizon?.horizon_start && horizon?.horizon_end
      ? `${horizon.horizon_start} – ${horizon.horizon_end}`
      : null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{t('consensus.scenarioComparisonTitle')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {viewMode === 'macro'
              ? t('consensus.scenarioComparisonHintMacro')
              : t('consensus.scenarioComparisonHintMonthly')}
          </p>
          {horizonLabel ? (
            <p className="mt-1 text-[11px] text-gray-400">
              {t('consensus.scenarioHorizon')}: {horizonLabel}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onToggleCreateForm ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50"
              onClick={onToggleCreateForm}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('consensus.newScenario')}
            </button>
          ) : null}
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                metric === 'units'
                  ? 'bg-stocker-primary/10 text-stocker-primary'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setMetric('units')}
            >
              {t('consensus.scenarioMetricUnits')}
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                metric === 'value'
                  ? 'bg-stocker-primary/10 text-stocker-primary'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setMetric('value')}
            >
              {t('consensus.scenarioMetricValue', { currencySymbol: symbol })}
            </button>
          </div>
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                viewMode === 'macro'
                  ? 'bg-stocker-primary/10 text-stocker-primary'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('macro')}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              {t('consensus.scenarioViewMacro')}
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                viewMode === 'monthly'
                  ? 'bg-stocker-primary/10 text-stocker-primary'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('monthly')}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {t('consensus.scenarioViewMonthly')}
            </button>
          </div>
        </div>
      </div>

      {showCreateForm && onCreateScenario ? (
        <CreateScenarioForm
          saving={creatingScenario}
          onCancel={() => onToggleCreateForm?.()}
          onSubmit={onCreateScenario}
        />
      ) : null}

      <div className="border-b border-gray-100 px-4 py-3">
        <ScenarioPicker
          available={available}
          selectedIds={selectedScenarioIds}
          onChange={onSelectedScenarioIdsChange}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 min-w-[200px] border-r border-gray-100 bg-gray-50 px-3 py-2">
                {t('sopScenarios.title')}
              </th>
              {periodKeys.map((period) => (
                <th key={period} className="whitespace-nowrap px-2 py-2 text-right">
                  {viewMode === 'macro'
                    ? formatMacroPeriodLabel(
                        period,
                        columnMeta.get(period)?.granularity,
                        t,
                        i18n.language,
                      )
                    : formatChartBucketLabel(period, 'month', i18n.language)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={periodKeys.length + 1} className="px-3 py-8 text-center text-gray-500">
                  {t('common.loading')}
                </td>
              </tr>
            ) : periodKeys.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(periodKeys.length + 1, 2)}
                  className="px-3 py-8 text-center text-gray-500"
                >
                  {t('consensus.scenarioNoComparisonData')}
                </td>
              </tr>
            ) : (
              allRows.map((row, index) => (
                <ScenarioDataRow
                  key={row.scenario_id ?? `base-${index}`}
                  row={row}
                  periodKeys={periodKeys}
                  columnMeta={columnMeta}
                  stripe={index % 2 === 1}
                  viewMode={viewMode}
                  metric={metric}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
